import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Tag,
  Button,
  Select,
  Input,
  Card,
  Statistic,
  Row,
  Col,
  Space,
  Tooltip,
  Typography,
  Drawer,
  Descriptions,
  Timeline as AntTimeline,
  Alert,
  Badge,
  Collapse,
  Empty,
  Spin,
  theme,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ReloadOutlined,
  EyeOutlined,
  BugOutlined,
  SoundOutlined,
  DesktopOutlined,
  ThunderboltOutlined,
  VideoCameraOutlined,
  AppleOutlined,
  AndroidOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import {
  getGameDiagnostics,
  getGameDiagnosticById,
  getGameDiagnosticSummary,
} from "../../../services/game-diagnostics/game-diagnostics.service";
import {
  GameDiagnosticListItem,
  GameDiagnosticFull,
  GameDiagnosticSummary,
} from "../../../services/game-diagnostics/game-diagnostics.types";
import PageHeader from "../../../components/PageHeader";

const { Text } = Typography;
const { Panel } = Collapse;

// KPI accents without an antd token equivalent, named so no bare hex is
// sprinkled through the JSX (the rest come from theme.useToken()).
const ANDROID_BRAND_GREEN = "#3ddc84";
const FREEZE_VOLCANO = "#ff7a45"; // antd volcano-5

// Intentional fixed dark "code viewer" palette for the raw report <pre>;
// it reads as console output and stays dark in both themes.
const LOG_VIEWER_BG = "#1e1e1e";
const LOG_VIEWER_FG = "#d4d4d4";

// ─── Helpers ────────────────────────────────────────────────────────

function msToTimestamp(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
}

function getPlatformIcon(platform: string) {
  return platform === "ios" ? <AppleOutlined /> : <AndroidOutlined />;
}

function getErrorCount(item: GameDiagnosticListItem): number {
  let count = 0;
  if (item.video_errors?.length) count += item.video_errors.length;
  if (item.screen_issues?.length) count += item.screen_issues.length;
  if (item.freezes?.length) count += item.freezes.length;
  if (item.audio_errors?.length) count += item.audio_errors.length;
  return count;
}

function getSeverityColor(item: GameDiagnosticListItem): string {
  const errors = getErrorCount(item);
  if (errors === 0) return "green";
  if (errors <= 2) return "orange";
  return "red";
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── Timeline Event Colors ─────────────────────────────────────────

function getTimelineColor(type: string): string {
  if (type.includes("ERROR") || type.includes("FAIL")) return "red";
  if (
    type.includes("WARN") ||
    type.includes("STALLED") ||
    type.includes("FROZEN") ||
    type.includes("DEAD_ZONE")
  )
    return "orange";
  if (
    type.includes("PLAYING") ||
    type.includes("READY") ||
    type.includes("UNLOCKED")
  )
    return "green";
  return "blue";
}

function getTimelineIcon(type: string) {
  if (type.includes("VIDEO")) return <VideoCameraOutlined />;
  if (type.includes("AUDIO")) return <SoundOutlined />;
  if (type.includes("SCREEN")) return <DesktopOutlined />;
  if (type.includes("FREEZE") || type.includes("FROZEN"))
    return <ThunderboltOutlined />;
  if (type.includes("ERROR") || type.includes("FAIL"))
    return <CloseCircleOutlined />;
  if (type.includes("TOUCH")) return <ExclamationCircleOutlined />;
  return <ClockCircleOutlined />;
}

// ─── Main Component ────────────────────────────────────────────────

const GameDiagnostics: React.FC = () => {
  const { token } = theme.useToken();
  const [diagnostics, setDiagnostics] = useState<GameDiagnosticListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [summary, setSummary] = useState<GameDiagnosticSummary | null>(null);
  const [selectedDiag, setSelectedDiag] = useState<GameDiagnosticFull | null>(
    null,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Filters
  const [platform, setPlatform] = useState<string | undefined>();
  const [searchUrl, setSearchUrl] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        getGameDiagnostics({
          platform,
          game_url: searchUrl || undefined,
          limit: pageSize,
          offset: (page - 1) * pageSize,
        }),
        getGameDiagnosticSummary(),
      ]);
      setDiagnostics(listRes.data);
      setTotal(listRes.total);
      setSummary(summaryRes);
      setError(false);
    } catch (e) {
      // A failed fetch must never render as an all-zero dashboard (false
      // negative on a monitoring page) — surface it instead.
      console.error(e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [platform, searchUrl, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewDetail = async (id: number) => {
    setDrawerOpen(true);
    setDrawerLoading(true);
    try {
      const detail = await getGameDiagnosticById(id);
      setSelectedDiag(detail);
    } finally {
      setDrawerLoading(false);
    }
  };

  // ─── Summary Cards ────────────────────────────────────────────────

  const totalVideoErrors = summary
    ? Object.values(summary.games_with_errors).reduce(
        (acc, g) => acc + g.video_errors,
        0,
      )
    : 0;
  const totalAudioErrors = summary
    ? Object.values(summary.games_with_errors).reduce(
        (acc, g) => acc + g.audio_errors,
        0,
      )
    : 0;
  const totalScreenIssues = summary
    ? Object.values(summary.games_with_errors).reduce(
        (acc, g) => acc + g.screen_issues,
        0,
      )
    : 0;
  const totalFreezes = summary
    ? Object.values(summary.games_with_errors).reduce(
        (acc, g) => acc + g.freezes,
        0,
      )
    : 0;

  // ─── Table Columns ────────────────────────────────────────────────

  const columns: ColumnsType<GameDiagnosticListItem> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Fecha",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      render: (val: string) => formatDate(val),
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      defaultSortOrder: "descend",
    },
    {
      title: "Plataforma",
      dataIndex: "platform",
      key: "platform",
      width: 110,
      render: (val: string) => (
        <Tag
          icon={getPlatformIcon(val)}
          color={val === "ios" ? "default" : "green"}
        >
          {val.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "URL del Juego",
      dataIndex: "game_url",
      key: "game_url",
      ellipsis: true,
      render: (val: string) => (
        <Tooltip title={val}>
          <Text style={{ maxWidth: 250 }} ellipsis>
            {val.replace(/https?:\/\//, "").replace(/\/$/, "")}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Usuario",
      dataIndex: "user_id",
      key: "user_id",
      width: 90,
    },
    {
      title: "Duración",
      dataIndex: "session_duration_ms",
      key: "session_duration_ms",
      width: 100,
      render: (val: number | null) =>
        val ? msToTimestamp(val) : <Text type="secondary">-</Text>,
    },
    {
      title: "Errores",
      key: "errors",
      width: 200,
      render: (_, record) => {
        const videoErrCount = record.video_errors?.length || 0;
        const screenCount = record.screen_issues?.length || 0;
        const freezeCount = record.freezes?.length || 0;
        const audioErrCount = record.audio_errors?.length || 0;
        const totalErrors =
          videoErrCount + screenCount + freezeCount + audioErrCount;

        if (totalErrors === 0) {
          return (
            <Tag icon={<CheckCircleOutlined />} color="success">
              Sin errores
            </Tag>
          );
        }

        return (
          <Space size={4} wrap>
            {videoErrCount > 0 && (
              <Tag icon={<VideoCameraOutlined />} color="error">
                {videoErrCount} video
              </Tag>
            )}
            {screenCount > 0 && (
              <Tag icon={<DesktopOutlined />} color="warning">
                {screenCount} pantalla
              </Tag>
            )}
            {freezeCount > 0 && (
              <Tag icon={<ThunderboltOutlined />} color="volcano">
                {freezeCount} freeze
              </Tag>
            )}
            {audioErrCount > 0 && (
              <Tag icon={<SoundOutlined />} color="magenta">
                {audioErrCount} audio
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "Severidad",
      key: "severity",
      width: 100,
      render: (_, record) => {
        const color = getSeverityColor(record);
        const errors = getErrorCount(record);
        return (
          <Badge
            status={
              color === "green"
                ? "success"
                : color === "orange"
                  ? "warning"
                  : "error"
            }
            text={errors === 0 ? "OK" : `${errors} issues`}
          />
        );
      },
      sorter: (a, b) => getErrorCount(a) - getErrorCount(b),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => handleViewDetail(record.id)}
        >
          Ver
        </Button>
      ),
    },
  ];

  // ─── Render ──────────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <PageHeader
        title="Diagnósticos de Juegos"
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading}
          >
            Actualizar
          </Button>
        }
      />

      {/* Fetch failure: show the error instead of an all-zero dashboard. */}
      {error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar los diagnósticos."
          action={
            <Button size="small" onClick={fetchData}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <>
          {/* Summary Cards */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={4}>
              <Card size="small">
                <Statistic
                  title="Total Reportes"
                  value={summary?.total_reports || 0}
                  valueStyle={{ color: token.colorPrimary }}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small">
                <Statistic
                  title="iOS"
                  value={summary?.by_platform?.ios || 0}
                  prefix={<AppleOutlined />}
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small">
                <Statistic
                  title="Android"
                  value={summary?.by_platform?.android || 0}
                  prefix={<AndroidOutlined />}
                  valueStyle={{ color: ANDROID_BRAND_GREEN }}
                />
              </Card>
            </Col>
            <Col span={3}>
              <Card size="small">
                <Statistic
                  title="Errores Video"
                  value={totalVideoErrors}
                  prefix={<VideoCameraOutlined />}
                  valueStyle={{
                    color:
                      totalVideoErrors > 0
                        ? token.colorError
                        : token.colorSuccess,
                  }}
                />
              </Card>
            </Col>
            <Col span={3}>
              <Card size="small">
                <Statistic
                  title="Errores Audio"
                  value={totalAudioErrors}
                  prefix={<SoundOutlined />}
                  valueStyle={{
                    color:
                      totalAudioErrors > 0
                        ? token.colorError
                        : token.colorSuccess,
                  }}
                />
              </Card>
            </Col>
            <Col span={3}>
              <Card size="small">
                <Statistic
                  title="Pantalla"
                  value={totalScreenIssues}
                  prefix={<DesktopOutlined />}
                  valueStyle={{
                    color:
                      totalScreenIssues > 0
                        ? token.colorWarning
                        : token.colorSuccess,
                  }}
                />
              </Card>
            </Col>
            <Col span={3}>
              <Card size="small">
                <Statistic
                  title="Freezes"
                  value={totalFreezes}
                  prefix={<ThunderboltOutlined />}
                  valueStyle={{
                    color:
                      totalFreezes > 0 ? FREEZE_VOLCANO : token.colorSuccess,
                  }}
                />
              </Card>
            </Col>
          </Row>

          {/* Filters */}
          <Space style={{ marginBottom: 16 }} wrap>
            <Input.Search
              placeholder="Buscar por URL del juego..."
              allowClear
              style={{ width: 300 }}
              onSearch={(val) => {
                setSearchUrl(val);
                setPage(1);
              }}
            />
            <Select
              style={{ width: 150 }}
              placeholder="Plataforma"
              allowClear
              value={platform}
              onChange={(val) => {
                setPlatform(val);
                setPage(1);
              }}
              options={[
                { value: "ios", label: "iOS" },
                { value: "android", label: "Android" },
              ]}
            />
          </Space>

          {/* Table */}
          <Table
            dataSource={diagnostics}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
              showTotal: (t) => `${t} reportes`,
            }}
            size="middle"
            scroll={{ x: 1100 }}
            rowClassName={(record) => {
              const errors = getErrorCount(record);
              if (errors > 2) return "diagnostic-row-error";
              if (errors > 0) return "diagnostic-row-warning";
              return "";
            }}
          />
        </>
      )}

      {/* Detail Drawer */}
      <Drawer
        title={
          selectedDiag ? (
            <Space>
              <BugOutlined />
              <span>Reporte #{selectedDiag.id}</span>
              <Tag
                color={selectedDiag.platform === "ios" ? "default" : "green"}
                icon={getPlatformIcon(selectedDiag.platform)}
              >
                {selectedDiag.platform.toUpperCase()}
              </Tag>
            </Space>
          ) : (
            "Detalle de Diagnóstico"
          )
        }
        placement="right"
        width={720}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedDiag(null);
        }}
        open={drawerOpen}
      >
        {drawerLoading ? (
          <div style={{ textAlign: "center", padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : selectedDiag ? (
          <DiagnosticDetail diag={selectedDiag} />
        ) : (
          <Empty description="No se encontró el reporte" />
        )}
      </Drawer>
    </div>
  );
};

// ─── Detail Component ──────────────────────────────────────────────

const DiagnosticDetail: React.FC<{ diag: GameDiagnosticFull }> = ({ diag }) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* General Info */}
      <Descriptions
        title="Información General"
        bordered
        size="small"
        column={2}
      >
        <Descriptions.Item label="ID">{diag.id}</Descriptions.Item>
        <Descriptions.Item label="Usuario">{diag.user_id}</Descriptions.Item>
        <Descriptions.Item label="Recurso ID">
          {diag.resource_id || <Text type="secondary">N/A</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Plataforma">
          <Tag
            icon={getPlatformIcon(diag.platform)}
            color={diag.platform === "ios" ? "default" : "green"}
          >
            {diag.platform.toUpperCase()}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="URL del Juego" span={2}>
          <a href={diag.game_url} target="_blank" rel="noopener noreferrer">
            {diag.game_url}
          </a>
        </Descriptions.Item>
        <Descriptions.Item label="Dispositivo" span={2}>
          {diag.device_info || <Text type="secondary">No disponible</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Duración Sesión">
          {diag.session_duration_ms
            ? msToTimestamp(diag.session_duration_ms)
            : "N/A"}
        </Descriptions.Item>
        <Descriptions.Item label="Fecha">
          {formatDate(diag.created_at)}
        </Descriptions.Item>
      </Descriptions>

      {/* Formats Supported */}
      <Collapse defaultActiveKey={["errors"]}>
        <Panel header="Formatos Soportados" key="formats">
          <Row gutter={[8, 8]}>
            {diag.formats_supported &&
              Object.entries(diag.formats_supported).map(([fmt, supported]) => (
                <Col span={12} key={fmt}>
                  <Tag
                    color={supported ? "success" : "error"}
                    icon={
                      supported ? (
                        <CheckCircleOutlined />
                      ) : (
                        <CloseCircleOutlined />
                      )
                    }
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "4px 8px",
                    }}
                  >
                    {fmt}
                  </Tag>
                </Col>
              ))}
          </Row>
        </Panel>

        {/* Video Errors */}
        <Panel
          header={
            <Space>
              <VideoCameraOutlined />
              <span>Errores de Video</span>
              {diag.video_errors?.length ? (
                <Badge count={diag.video_errors.length} />
              ) : (
                <Tag color="success">0</Tag>
              )}
            </Space>
          }
          key="errors"
        >
          {diag.video_errors && diag.video_errors.length > 0 ? (
            diag.video_errors.map((err, i) => (
              <Alert
                key={i}
                type="error"
                showIcon
                style={{ marginBottom: 8 }}
                message={`Video #${err.videoId} — ${err.codeLabel}`}
                description={
                  <div>
                    <div>
                      <Text strong>Source:</Text> <Text code>{err.src}</Text>
                    </div>
                    <div>
                      <Text strong>Código:</Text> {err.code} ({err.codeLabel})
                    </div>
                    <div>
                      <Text strong>Mensaje:</Text> {err.message}
                    </div>
                    <div>
                      <Text strong>Tiempo:</Text> {msToTimestamp(err.elapsed)}
                    </div>
                  </div>
                }
              />
            ))
          ) : (
            <Text type="success">
              <CheckCircleOutlined /> Sin errores de video
            </Text>
          )}
        </Panel>

        {/* Audio Errors */}
        <Panel
          header={
            <Space>
              <SoundOutlined />
              <span>Errores de Audio</span>
              {diag.audio_errors?.length ? (
                <Badge count={diag.audio_errors.length} />
              ) : (
                <Tag color="success">0</Tag>
              )}
            </Space>
          }
          key="audio"
        >
          {diag.audio_errors && diag.audio_errors.length > 0 ? (
            diag.audio_errors.map((err, i) => (
              <Alert
                key={i}
                type="warning"
                showIcon
                style={{ marginBottom: 8 }}
                message={err.type}
                description={
                  <div>
                    <div>
                      <Text strong>Detalle:</Text>{" "}
                      {err.message || err.detail || "Sin detalle"}
                    </div>
                    <div>
                      <Text strong>Tiempo:</Text> {msToTimestamp(err.elapsed)}
                    </div>
                  </div>
                }
              />
            ))
          ) : (
            <Text type="success">
              <CheckCircleOutlined /> Sin errores de audio
            </Text>
          )}
        </Panel>

        {/* Screen Issues */}
        <Panel
          header={
            <Space>
              <DesktopOutlined />
              <span>Problemas de Pantalla</span>
              {diag.screen_issues?.length ? (
                <Badge count={diag.screen_issues.length} />
              ) : (
                <Tag color="success">0</Tag>
              )}
            </Space>
          }
          key="screen"
        >
          {diag.screen_issues && diag.screen_issues.length > 0 ? (
            diag.screen_issues.map((issue, i) => (
              <Alert
                key={i}
                type={issue.confidence === "low" ? "info" : "warning"}
                showIcon
                icon={<DesktopOutlined />}
                style={{ marginBottom: 8 }}
                message={
                  <Space>
                    {issue.type}
                    {issue.confidence && (
                      <Tag
                        color={issue.confidence === "high" ? "red" : "default"}
                      >
                        Confianza: {issue.confidence}
                      </Tag>
                    )}
                  </Space>
                }
                description={
                  <div>
                    <div>
                      <Text strong>Duración:</Text>{" "}
                      {msToTimestamp(issue.duration)}
                    </div>
                    <div>
                      <Text strong>Luminosidad promedio:</Text>{" "}
                      {issue.lumAvg?.toFixed(1)}
                    </div>
                    <div>
                      <Text strong>Varianza:</Text>{" "}
                      {issue.lumVariance?.toFixed(1)}
                    </div>
                    {issue.startedAt !== undefined && (
                      <div>
                        <Text strong>Inicio:</Text>{" "}
                        {msToTimestamp(issue.startedAt)}
                      </div>
                    )}
                    {issue.occurrences !== undefined &&
                      issue.occurrences > 1 && (
                        <div>
                          <Text strong>Ocurrencias:</Text> {issue.occurrences}
                        </div>
                      )}
                    {issue.activeVideos !== undefined && (
                      <div>
                        <Text strong>Videos activos:</Text> {issue.activeVideos}
                      </div>
                    )}
                    {issue.audioPlaying !== undefined && (
                      <div>
                        <Text strong>Audio reproduciéndose:</Text>{" "}
                        {issue.audioPlaying ? "Sí" : "No"}
                      </div>
                    )}
                    {issue.detectedAt !== undefined && (
                      <div>
                        <Text strong>Detectado en:</Text>{" "}
                        {msToTimestamp(issue.detectedAt)}
                      </div>
                    )}
                  </div>
                }
              />
            ))
          ) : (
            <Text type="success">
              <CheckCircleOutlined /> Sin problemas de pantalla
            </Text>
          )}
        </Panel>

        {/* Freezes */}
        <Panel
          header={
            <Space>
              <ThunderboltOutlined />
              <span>Freezes</span>
              {diag.freezes?.length ? (
                <Badge count={diag.freezes.length} />
              ) : (
                <Tag color="success">0</Tag>
              )}
            </Space>
          }
          key="freezes"
        >
          {diag.freezes && diag.freezes.length > 0 ? (
            diag.freezes.map((freeze, i) => (
              <Alert
                key={i}
                type="error"
                showIcon
                icon={<ThunderboltOutlined />}
                style={{ marginBottom: 8 }}
                message={`Freeze de ${msToTimestamp(freeze.duration)}`}
                description={
                  <div>
                    <Text strong>Detectado en:</Text>{" "}
                    {msToTimestamp(freeze.detectedAt)}
                  </div>
                }
              />
            ))
          ) : (
            <Text type="success">
              <CheckCircleOutlined /> Sin freezes
            </Text>
          )}
        </Panel>

        {/* Performance */}
        <Panel
          header={
            <Space>
              <ThunderboltOutlined />
              <span>Performance</span>
            </Space>
          }
          key="performance"
        >
          {diag.performance ? (
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="FPS Promedio">
                <Tag
                  color={
                    (diag.performance.fps_avg ?? 0) >= 30
                      ? "success"
                      : (diag.performance.fps_avg ?? 0) >= 15
                        ? "warning"
                        : "error"
                  }
                >
                  {diag.performance.fps_avg ?? "N/A"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="FPS Mínimo">
                {diag.performance.fps_min ?? "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="FPS Actual">
                {diag.performance.fps_current ?? "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="AudioContext">
                <Tag
                  color={
                    diag.performance.audio_context_state === "running"
                      ? "success"
                      : "warning"
                  }
                >
                  {diag.performance.audio_context_state}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Audio Desbloqueado">
                {diag.performance.audio_unlocked ? (
                  <Tag color="success">
                    SÍ ({diag.performance.audio_unlock_time_ms}ms)
                  </Tag>
                ) : (
                  <Tag color="error">NO</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Sources Audio">
                {diag.performance.audio_active_sources} activos /{" "}
                {diag.performance.audio_total_sources} total
              </Descriptions.Item>
              <Descriptions.Item label="Decode Intentos">
                {diag.performance.audio_decode_attempts}
              </Descriptions.Item>
              <Descriptions.Item label="Decode Fallos">
                <Text
                  type={
                    diag.performance.audio_decode_fails > 0
                      ? "danger"
                      : "success"
                  }
                >
                  {diag.performance.audio_decode_fails}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Touch Dead Zones">
                <Text
                  type={
                    diag.performance.touch_dead_zones > 0
                      ? "warning"
                      : "success"
                  }
                >
                  {diag.performance.touch_dead_zones}
                </Text>
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <Text type="secondary">Sin datos de performance</Text>
          )}
        </Panel>

        {/* Timeline */}
        <Panel
          header={
            <Space>
              <ClockCircleOutlined />
              <span>Timeline de Eventos</span>
              <Tag>{diag.timeline?.length || 0} eventos</Tag>
            </Space>
          }
          key="timeline"
        >
          {diag.timeline && diag.timeline.length > 0 ? (
            <div style={{ maxHeight: 500, overflowY: "auto" }}>
              <AntTimeline
                items={diag.timeline.map((entry, i) => ({
                  color: getTimelineColor(entry.type),
                  dot: getTimelineIcon(entry.type),
                  children: (
                    <div key={i}>
                      <Space>
                        <Text code>{msToTimestamp(entry.elapsed)}</Text>
                        <Tag
                          color={getTimelineColor(entry.type)}
                          style={{ fontSize: 11 }}
                        >
                          {entry.type}
                        </Tag>
                      </Space>
                      {entry.payload &&
                        Object.keys(entry.payload).length > 0 && (
                          <div
                            style={{
                              marginTop: 4,
                              fontSize: 12,
                            }}
                          >
                            {Object.entries(entry.payload).map(([k, v]) => (
                              <div key={k}>
                                <Text type="secondary">{k}:</Text>{" "}
                                <Text>
                                  {typeof v === "object"
                                    ? JSON.stringify(v)
                                    : String(v)}
                                </Text>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  ),
                }))}
              />
            </div>
          ) : (
            <Empty description="Sin eventos en el timeline" />
          )}
        </Panel>

        {/* Raw Report */}
        <Panel
          header={
            <Space>
              <WarningOutlined />
              <span>Reporte en Texto</span>
            </Space>
          }
          key="report"
        >
          <pre
            style={{
              background: LOG_VIEWER_BG,
              color: LOG_VIEWER_FG,
              padding: 16,
              borderRadius: 8,
              fontSize: 12,
              lineHeight: 1.5,
              maxHeight: 500,
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {diag.report_text}
          </pre>
        </Panel>
      </Collapse>
    </div>
  );
};

export default GameDiagnostics;
